import jwtDecode from 'jwt-decode'
import router from '@/router'
import { isNull } from 'lodash'

// client side check if the token in the localStorage is expired.
const isTokenExpired = token => {
  // check if token exists
  if (token) {
    const decodeToken = jwtDecode(token)
    // check if token is expired
    if (decodeToken.exp > Date.now() / 1000) {
      return false
    }
  }
  return true
}

const getMenuLinks = obj => {
  let list = []
  const key = 'link'
  const children = 'items'

  if (!obj) return list

  if (obj instanceof Array) {
    for (const i in obj) {
      list = list.concat(getMenuLinks(obj[i]))
    }
    return list
  }

  if (obj[key]) {
    list.push({
      link: obj['link'],
      name: obj['name'],
    })
  }

  if (typeof obj === 'object' && obj[children] !== null) {
    list = list.concat(getMenuLinks(obj[children]))
  }

  return list
}

const state = {
  token: null,
  session: null,
  pending: {},
  profile: null,
  errors: {},
}

const getters = {
  isAuthenticated: state => !!state.token && !isTokenExpired(state.token),
  menus: state => state.session && state.session.menu,
  routes: state => {
    let routes = []
    if (state.session && state.session.menu) {
      routes = getMenuLinks(state.session.menu)
    }
    return routes
  },
  user: state => state.session && state.session.user,
}

const actions = {
  login({ commit, dispatch }, credentials) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true })

      // call api auth/login with data
      this.api.auth
        .login(credentials)
        .then(({ data }) => {
          // TODO: comment out for dev
          // if (isNull(data) || !data.success) {
          if (isNull(data)) {
            commit('LOGIN_FAIL', { msg: 'Login failed' })
            return
          }
          // call LOGIN_SUCCESS from mutations
          commit('LOGIN_SUCCESS', data)
          // action getSession
          dispatch('getSession').catch(err => err)
          resolve()
        })
        .catch(error => {
          commit('LOGIN_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false })
        })
    })
  },
  getSession({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getSession' })

      this.api.auth
        .getSession()
        .then(({ data }) => {
          commit('GET_SESSION_SUCCESS', data)
          dispatch('setRoutes')
          resolve()
        })
        .catch(error => {
          commit('GET_SESSION_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getSession' })
        })
    })
  },
  initAuth({ dispatch, commit }) {
    return new Promise(resolve => {
      // get token and session from localStorage
      const token = localStorage.getItem('token')
      const session = JSON.parse(localStorage.getItem('session'))

      if (token && session) {
        commit('INIT_AUTH', { token, session })
        dispatch('setRoutes')
        resolve()
      } else {
        dispatch('logout').catch(err => err)
      }
    })
  },
  logout({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true })

      this.api.auth
        .logout()
        .then(() => {
          commit('LOGOUT_SUCCESS')
          resolve()
        })
        .catch(error => {
          commit('LOGOUT_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false })
        })
    })
  },
  setRoutes({ getters }) {
    const routeLinks = getters.routes
    const routes = []

    const allRouteLinks = routeLinks.filter((value, index, self) => {
      return index === self.findIndex(route => (
        route.link === value.link && route.name === value.name
      ))
    })

    allRouteLinks.forEach(routeLink => {
      const routeName = routeLink.link.replace('/', '')

      const route = {
        path: routeLink.link,
        meta: {
          title: routeLink.name,
          type: routeName.charAt(0).toLowerCase() + routeName.slice(1),
          requiresAuth: true,
        },
        props: true,
        name: routeName.charAt(0).toLowerCase() + routeName.slice(1),
      }

      const listRoute = Object.assign({}, route)
      try {
        listRoute.component = () => import(`@/views/${routeName}/${routeName}Overview`)
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.log(`Not found view: ${routeName}`)
        listRoute.component = () => import('@/views/ErrorPages/View404')
      }

      const singleRoute = Object.assign({}, route)
      try {
        singleRoute.component = () => import(`@/views/${routeName}/${routeName}Item`)
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.log(`Not found view: ${routeName} Item`)
        singleRoute.component = () => import('@/views/ErrorPages/View404')
      }
      singleRoute.path += '/:id/:tab?'
      singleRoute.name += '-item'

      const singleCreateRoute = Object.assign({}, route)
      try {
        singleCreateRoute.component = () => import(`@/views/${routeName}/${routeName}ItemCreate`)
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.log(`Not found view: ${routeName} ItemCreate`)
        singleCreateRoute.component = () => import('@/views/ErrorPages/View404')
      }
      singleCreateRoute.path += '/create'
      singleCreateRoute.name += '-create'

      const singleEditRoute = Object.assign({}, route)
      try {
        singleEditRoute.component = () => import(`@/views/${routeName}/${routeName}ItemEdit`)
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.log(`Not found view: ${routeName} ItemEdit`)
        singleEditRoute.component = () => import('@/views/ErrorPages/View404')
      }
      singleEditRoute.path += '/edit/:id'
      singleEditRoute.name += '-edit'

      routes.push(listRoute, singleCreateRoute, singleEditRoute, singleRoute)
    })

    router.addRoutes(routes)
  },
  getProfile({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getProfile' })

      // TODO: own route for get profile?
      this.api.item
        .getItem('users', state.session.user)
        .then(({ data }) => {
          commit('GET_PROFILE_SUCCESS', data.user)
          resolve()
        })
        .catch(error => {
          commit('GET_PROFILE_ERROR', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getProfile' })
        })
    })
  },
}

const mutations = {
  SET_PENDING: (state, { pending, type = 'default' }) => {
    state.pending = { ...state.pending, [type]: pending }
  },
  INIT_AUTH(state, { token, session }) {
    state.errors = {}
    state.session = session
    state.token = token
  },
  LOGIN_SUCCESS(state, { token }) {
    state.errors = {}
    state.token = token
    localStorage.setItem('token', token)
    state.error = null
  },
  LOGIN_FAIL(state, errors) {
    state.errors = errors
  },
  GET_SESSION_SUCCESS(state, session) {
    state.errors = {}
    localStorage.setItem('session', JSON.stringify(session))
    state.session = session
  },
  GET_SESSION_FAIL(state, errors) {
    state.errors = errors
    state.token = null
    state.session = null
    localStorage.removeItem('token')
    localStorage.removeItem('session')
  },
  LOGOUT_SUCCESS(state) {
    state.errors = {}
    state.token = null
    state.session = null
    localStorage.removeItem('token')
    localStorage.removeItem('session')
  },
  LOGOUT_FAIL(state, errors) {
    state.errors = errors
    state.token = null
    state.session = null
    localStorage.removeItem('token')
    localStorage.removeItem('session')
  },
  GET_PROFILE_SUCCESS(state, profile) {
    state.profile = profile
    state.errors = {}
  },
  GET_PROFILE_ERROR(state, errors) {
    state.errors = errors
  },
  CLEAR_PROFILE(state) {
    state.profile = null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
